export const COUNTRY_CODES = ['CL', 'AR', 'PE', 'CR', 'GT', 'MX', 'UY', 'CO', 'SV'] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

interface CountryConfig {
  label: string;
  prefix: string;
  flag: string;
  phonePattern: RegExp;
  /** Municipal-division name. Keep in sync with backend `countriesRecord.communeLabel`. */
  communeLabel: string;
}

export const COUNTRY_CONFIG = {
  CL: { label: 'Chile', prefix: '56', flag: '🇨🇱', phonePattern: /^56[29]\d{8}$/, communeLabel: 'Comuna' },
  AR: { label: 'Argentina', prefix: '54', flag: '🇦🇷', phonePattern: /^549\d{8,12}$/, communeLabel: 'Localidad' },
  PE: { label: 'Perú', prefix: '51', flag: '🇵🇪', phonePattern: /^51\d{9}$/, communeLabel: 'Distrito' },
  CR: { label: 'Costa Rica', prefix: '506', flag: '🇨🇷', phonePattern: /^506\d{8}$/, communeLabel: 'Cantón' },
  GT: { label: 'Guatemala', prefix: '502', flag: '🇬🇹', phonePattern: /^502\d{8}$/, communeLabel: 'Municipio' },
  MX: {
    label: 'México',
    prefix: '52',
    flag: '🇲🇽',
    phonePattern: /^(?:52\d{10}|521\d{10})$/,
    communeLabel: 'Alcaldía o Municipio',
  },
  UY: { label: 'Uruguay', prefix: '598', flag: '🇺🇾', phonePattern: /^598\d{8}$/, communeLabel: 'Localidad' },
  CO: { label: 'Colombia', prefix: '57', flag: '🇨🇴', phonePattern: /^57\d{10}$/, communeLabel: 'Municipio' },
  SV: { label: 'El Salvador', prefix: '503', flag: '🇸🇻', phonePattern: /^503\d{8}$/, communeLabel: 'Municipio' },
} as const satisfies Record<CountryCode, CountryConfig>;

export const COUNTRY_OPTIONS = COUNTRY_CODES.map((code) => ({
  code,
  ...COUNTRY_CONFIG[code],
}));

export const DEFAULT_COUNTRY_CODE: CountryCode = 'CL';

export const isCountryCode = (value: unknown): value is CountryCode =>
  typeof value === 'string' && (COUNTRY_CODES as readonly string[]).includes(value);

/** Customer-facing municipal-division label. Unknown/missing codes fall back to Chile's "Comuna". */
export const getCommuneLabel = (countryCode?: string | null): string => {
  if (isCountryCode(countryCode)) return COUNTRY_CONFIG[countryCode].communeLabel;
  return COUNTRY_CONFIG[DEFAULT_COUNTRY_CODE].communeLabel;
};
